/**
 * Proactive token refresh plugin.
 *
 * Reads kcTokenExpiry cookie and schedules refresh 60 seconds before expiry.
 * On successful refresh, reschedules the next refresh.
 * On refresh token expiry, redirects to login step.
 *
 * Skips entirely in webview context where native app handles token management.
 *
 * Part of the token refresh mechanism (CCT-2060).
 */

import { checkIsWebView } from '@camelot/webview-utils'
import * as Sentry from '@sentry/vue'

const REFRESH_BUFFER_SECONDS = 60

export default defineNuxtPlugin(() => {
  // Skip in webview - it has its own refresh logic via @camelot/webview-utils
  if (typeof window !== 'undefined' && checkIsWebView(window)) {
    return
  }

  let refreshTimeoutId: ReturnType<typeof setTimeout> | undefined

  function getCookieValue(name: string): string | null {
    if (typeof document === 'undefined')
      return null

    const cookies = document.cookie.split('; ')
    const cookie = cookies.find(c => c.startsWith(`${name}=`))
    return cookie ? decodeURIComponent(cookie.split('=')[1]) : null
  }

  async function refreshToken(): Promise<void> {
    try {
      await $fetch('/api/auth/refresh', {
        method: 'POST',
        retry: 1,
      })

      // Refresh succeeded - schedule next refresh
      scheduleTokenRefresh()
    }
    catch (error: any) {
      if (error?.data?.requiresReauth) {
        // Refresh token expired - redirect to login
        Sentry.captureMessage(
          'Refresh token expired, redirecting to login',
          'info',
        )

        const route = useRoute()
        const lang = route.params._lang || 'en'
        const city = route.params._city || 'berlin'

        await navigateTo(`/${lang}/${city}/personal`, {
          replace: true,
        })
      }
      else {
        // Network error or other issue - log but don't redirect
        Sentry.captureException(error)
      }
    }
  }

  function scheduleTokenRefresh(): void {
    // Clear any existing timeout
    if (refreshTimeoutId) {
      clearTimeout(refreshTimeoutId)
      refreshTimeoutId = undefined
    }

    const expiryStr = getCookieValue('kcTokenExpiry')
    if (!expiryStr) {
      // No token expiry cookie - either logged out or token already expired
      return
    }

    const expiryTimestamp = Number.parseInt(expiryStr, 10)
    const now = Math.floor(Date.now() / 1000)
    const secondsUntilExpiry = expiryTimestamp - now

    if (secondsUntilExpiry <= 0) {
      // Token already expired
      return
    }

    const secondsUntilRefresh = Math.max(
      secondsUntilExpiry - REFRESH_BUFFER_SECONDS,
      5, // Minimum 5 seconds delay
    )

    refreshTimeoutId = setTimeout(() => {
      refreshToken()
    }, secondsUntilRefresh * 1000)
  }

  // Schedule initial refresh on plugin load
  onNuxtReady(() => {
    scheduleTokenRefresh()
  })

  // Re-schedule after navigation (cookie may have changed after OIDC redirect)
  const router = useRouter()
  router.afterEach(() => {
    scheduleTokenRefresh()
  })

  // Listen for refresh events from other tabs
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
      // When kcTokenExpiry changes in another tab, reschedule
      if (event.key === 'kcTokenExpiry' && event.newValue) {
        scheduleTokenRefresh()
      }
    })

    // Clean up on unmount
    window.addEventListener('beforeunload', () => {
      if (refreshTimeoutId) {
        clearTimeout(refreshTimeoutId)
      }
    })
  }
})
